import { UploadAndClassifyFileResponse, UploadFileError } from '@frame/lib/api/tasks/types'

export enum ErrorCode {
  DUPLICATE_FILE = 'duplicate-file',
  TOO_MANY_FILES = 'too-many-files',
  FILE_TOO_LARGE = 'file-too-large',
  FILE_INVALID_TYPE = 'file-invalid-type',
  OTHER = 'other',
}

export enum IncomeSource {
  GENERIC_INCOME = 'Income',
  OTHER_INCOME = 'Other income',
}

export enum FileType {
  CONSUMER_DOCUMENT = 'consumer-document',
}

export enum FileUploadState {
  PROCESSING = 'processing',
  FAILED = 'failed',
  COMPLETED = 'completed',
}

export type TrackedFile = {
  tempId: string
  state: FileUploadState
  file?: File
  uploadedData?: UploadAndClassifyFileResponse
  error?: UploadFileError['file']
  classificationResults?: ClassificationResult[]
  isRetryable: boolean
}

export type FileStatus = {
  fileName: string
  state: FileUploadState
  error?: string
  fileId: string
}

export type FileStatusMap = {
  [key: string]: FileStatus
}

export enum OtherDocumentType {
  ALIMONY = 'alimony',
  CHILD_SUPPORT = 'child-support',
  DISABILITY_BENEFITS = 'disability-benefits',
  VA_BENEFITS = 'va-benefits',
  HOUSING_VOUCHER = 'housing-voucher',
  OFFER_LETTER = 'offer-letter',
  OTHER = 'other',
}

export enum DocumentTypeOption {
  PAYSTUB_OR_W2 = 'paystub_or_w2',
  OTHER = 'other',
  NONE = 'none',
  OFFER_LETTER = 'offer_letter',
}

export enum ClassificationResult {
  A_1005 = '1005',
  A_1040_2023 = 'A_1040_2023',
  A_1040_2024 = 'A_1040_2024',
  A_1040_SCHEDULE_1_2023 = 'A_1040_SCHEDULE_1_2023',
  A_1040_SCHEDULE_1_2024 = 'A_1040_SCHEDULE_1_2024',
  A_1040_SCHEDULE_2_2024 = 'A_1040_SCHEDULE_2_2024',
  A_1040_SCHEDULE_3_2023 = 'A_1040_SCHEDULE_3_2023',
  A_1040_SCHEDULE_3_2024 = 'A_1040_SCHEDULE_3_2024',
  A_1040_SCHEDULE_8812_2023 = 'A_1040_SCHEDULE_8812_2023',
  A_1040_SCHEDULE_8812_2024 = 'A_1040_SCHEDULE_8812_2024',
  A_1040_SCHEDULE_A_2024 = 'A_1040_SCHEDULE_A_2024',
  A_1040_SCHEDULE_B_2024 = 'A_1040_SCHEDULE_B_2024',
  A_1040_SCHEDULE_C_2024 = 'A_1040_SCHEDULE_C_2024',
  A_1040_SCHEDULE_D_2024 = 'A_1040_SCHEDULE_D_2024',
  A_1040_SCHEDULE_EIC_2024 = 'A_1040_SCHEDULE_EIC_2024',
  A_1040_SCHEDULE_SE_2024 = 'A_1040_SCHEDULE_SE_2024',
  A_1065_SCHEDULE_K_1_2024 = 'A_1065_SCHEDULE_K_1_2024',
  A_1099_G = 'A_1099_G',
  A_1099_INT = 'A_1099_INT',
  A_1099_K = 'A_1099_K',
  A_1099_MISC = 'A_1099_MISC',
  A_1099_NEC = 'A_1099_NEC',
  A_1099_R = 'A_1099_R',
  A_4562 = 'A_4562',
  A_8863 = 'A_8863',
  A_8867 = 'A_8867',
  A_8879 = 'A_8879',
  A_8962 = 'A_8962',
  A_8995 = 'A_8995',
  A_9325 = 'A_9325',
  A_941_X = 'A_941_X',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  BANK_STATEMENT = 'BANK_STATEMENT',
  BROKERAGE_STATEMENT = 'BROKERAGE_STATEMENT',
  CHARITABLE_DEDUCTION_LIMITS_WORKSHEET = 'CHARITABLE_DEDUCTION_LIMITS_WORKSHEET',
  COVER_PAGE = 'COVER_PAGE',
  CREDIT_CARD_STATEMENT = 'CREDIT_CARD_STATEMENT',
  DEPOSITED_CHECK = 'DEPOSITED_CHECK',
  DISABILITY_AWARD_LETTER = 'DISABILITY_AWARD_LETTER',
  ELECTRONIC_RECORD_AND_SIGNATURE_DISCLOSURE = 'ELECTRONIC_RECORD_AND_SIGNATURE_DISCLOSURE',
  FEDERAL_INFORMATION_WORKSHEET = 'FEDERAL_INFORMATION_WORKSHEET',
  FEDERAL_TAX_RETURN_WORKSHEET = 'FEDERAL_TAX_RETURN_WORKSHEET',
  FILING_INSTRUCTIONS = 'FILING_INSTRUCTIONS',
  HOA_CCRS = 'HOA_CCRS',
  INCOMING_WIRE_DIRECT_DEPOSIT_CONFIRMATION = 'INCOMING_WIRE_DIRECT_DEPOSIT_CONFIRMATION',
  INVOICE = 'INVOICE',
  LETTER_OF_EXPLANATION = 'LETTER_OF_EXPLANATION',
  LLC_CORPORATION_DOCUMENTS = 'LLC_CORPORATION_DOCUMENTS',
  PAYOFF_STATEMENT = 'PAYOFF_STATEMENT',
  PAYSTUB = 'PAYSTUB',
  PROFIT_AND_LOSS = 'PROFIT_AND_LOSS',
  SOLAR_PANEL_LEASE_AGREEMENT = 'SOLAR_PANEL_LEASE_AGREEMENT',
  SS_AWARD_LETTER = 'SS_AWARD_LETTER',
  STATE_TAX_RETURN = 'STATE_TAX_RETURN',
  TAX_RETURN_TRANSCRIPT = 'TAX_RETURN_TRANSCRIPT',
  TRUST_AGREEMENT_OR_TRUST_CERTIFICATION = 'TRUST_AGREEMENT_OR_TRUST_CERTIFICATION',
  UNKNOWN = 'UNKNOWN',
  VA_AWARD_LETTER = 'VA_AWARD_LETTER',
  VERIFICATION_OF_EMPLOYMENT = 'VERIFICATION_OF_EMPLOYMENT',
  W2 = 'W2',
  OFFER_LETTER = 'OFFER_LETTER',
}

type UploadAndClassifyFileResultField = {
  page_index: number
  value: string
  confidence?: number
  normalized_text?: string
}

type UploadAndClassifyFileResultPaystubFields = {
  statement_beginning_date?: UploadAndClassifyFileResultField
  statement_ending_date?: UploadAndClassifyFileResultField
  statement_payment_date?: UploadAndClassifyFileResultField
  employer_name?: UploadAndClassifyFileResultField
  employee_first_name?: UploadAndClassifyFileResultField
  employee_last_name?: UploadAndClassifyFileResultField
}

type UploadAndClassifyFileResultW2Fields = {
  employer_name?: UploadAndClassifyFileResultField
  document_year?: UploadAndClassifyFileResultField
}

type UploadAndClassifyFileResultFields = {
  paystub?: UploadAndClassifyFileResultPaystubFields
  w2?: UploadAndClassifyFileResultW2Fields
}

export type UploadAndClassifyFileResult = {
  form_type: ClassificationResult
  page_indexes: number[]
  fields?: UploadAndClassifyFileResultFields
}
